import { useState, useEffect, useRef } from "react";
import SEO from '../components/SEO';

export default function FilmThemedErrorPage() {
  const cameraRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    let targetTiltX = 0;
    let targetTiltY = 0;
    let currentTiltX = 0;
    let currentTiltY = 0;

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      targetTiltX = ((clientY / innerHeight) - 0.5) * 15;
      targetTiltY = ((clientX / innerWidth) - 0.5) * -15;
    };

    const animateCamera = () => {
      currentTiltX += (targetTiltX - currentTiltX) * 0.1;
      currentTiltY += (targetTiltY - currentTiltY) * 0.1;
      
      if (cameraRef.current) {
        cameraRef.current.style.transform = 
          `perspective(1000px) 
           rotateX(${currentTiltX}deg) 
           rotateY(${currentTiltY}deg)
           ${isHovering ? 'scale(1.1)' : ''}`;
      }
      animationFrameRef.current = requestAnimationFrame(animateCamera);
    };

    animationFrameRef.current = requestAnimationFrame(animateCamera);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isHovering]);

return (
    <div className="film-error-container">
        <SEO
          title="Page Not Found"
          description="The page you are looking for could not be found on ProducerBuddy."
          url="https://producerbuddy.pilomation.com/404"
          robots="noindex, nofollow"
        />
        <div className="film-error-content">
            <div className="film-error-logo">
                <div className="film-error-logo-icon">
                    <span className="film-error-logo-text">PB</span>
                </div>
                <div className="film-error-logo-text-container">
                    <p className="film-error-logo-title">Producer Buddy</p>
                    <p className="film-error-logo-subtitle">A product of Pilomation</p>
                </div>
            </div>

            <div className="film-error-main">
                <div className="film-error-tag">SCENE MISSING</div>
                <h1 className="film-error-title">Error Occurred</h1>
                <p className="film-error-description">
                    The scene you're looking for seems to be missing from our production timeline.
                    Our crew is working to get everything back on track.
                </p>

                <div className="film-error-camera-container">
                    <div 
                        ref={cameraRef}
                        className="film-error-camera"
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    >
                        <svg viewBox="0 0 240 240" className="film-error-camera-svg">
                            <g fill="none" stroke="#e2e2e2" strokeWidth="2">
                                <path d="M120 160 L90 240 M120 160 L150 240 M120 160 L120 240" strokeWidth="3" />
                                <circle cx="120" cy="160" r="8" fill="#555" />
                                <rect x="80" y="100" width="80" height="60" rx="4" fill="#333" />
                                <rect x="95" y="85" width="50" height="15" rx="2" fill="#444" />
                                <circle cx="120" cy="130" r="20" fill="#222" />
                                <circle cx="120" cy="130" r="15" fill="#111" />
                                <circle cx="120" cy="130" r="10" fill="#000" />
                                <circle cx="118" cy="128" r="2" fill="#fff" />
                                <circle cx="60" cy="120" r="20" fill="#555" />
                                <circle cx="180" cy="120" r="20" fill="#555" />
                                <rect x="145" y="120" width="25" height="20" fill="#222" rx="2" />
                                <circle cx="185" cy="130" r="3" fill="#f8f8f8" />
                            </g>
                        </svg>
                    </div>
                </div>

                <div className="film-error-code">
                    <div className="film-error-code-badge">
                        <div className="film-error-code-indicator"></div>
                        <span className="film-error-code-text">404 Error - SCENE NOT FOUND</span>
                    </div>
                </div>

                <button 
                    className="film-error-button" 
                    onClick={() => window.location.href = '/'}
                >
                    <svg className="film-error-button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Return to Set
                </button>
            </div>

            <div className="film-error-footer">
                <p className="film-error-footer-text">© {new Date().getFullYear()} Pilomation. All rights reserved.</p>
            </div>
        </div>

        <style jsx>{`
            .film-error-container {
                position: relative;
                width: 100%;
                height: 100vh;
                overflow: hidden;
                background: linear-gradient(135deg, #0a0a12 7%,rgb(64, 209, 183) 93%);
                margin: 0;
                padding: 0;
            }
            
            .film-error-content {
                position: absolute;
                inset: 0;
                z-index: 10;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 1.5rem;
            }
            
            .film-error-logo {
                position: absolute;
                top: 1.5rem;
                left: 1.5rem;
                display: flex;
                align-items: center;
            }
            
            .film-error-logo-icon {
                width: 3rem;
                height: 3rem;
                background-color: rgb(55, 65, 81);
                border-radius: 0.375rem;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .film-error-logo-text {
                color: hsl(45, 100.00%, 51.00%);
                font-size: 1.25rem;
            }
            
            .film-error-logo-text-container {
                margin-left: 0.75rem;
                color: white;
            }
            
            .film-error-logo-title {
                font-size: 1.125rem;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin: 0;
            }
            
            .film-error-logo-subtitle {
                font-size: 0.75rem;
                color: rgb(156, 163, 175);
                margin: 0;
            }
            
            .film-error-main {
                text-align: center;
                max-width: 42rem;
                padding: 0 1rem;
            }
            
            .film-error-tag {
                color: rgb(234, 179, 8);
                font-weight: 300;
                margin-bottom: 0.75rem;
                letter-spacing: 0.1em;
            }
            
            .film-error-title {
                font-size: 3rem;
                line-height: 1;
                font-weight: 700;
                color: white;
                margin-bottom: 1.5rem;
            }
            
            @media (min-width: 768px) {
                .film-error-title {
                    font-size: 3.75rem;
                }
            }
            
            .film-error-description {
                color: rgb(209, 213, 219);
                margin-bottom: 2rem;
                font-size: 1.125rem;
                line-height: 1.75rem;
                max-width: 42rem;
                margin-left: auto;
                margin-right: auto;
            }
            
            @media (min-width: 768px) {
                .film-error-description {
                    font-size: 1.25rem;
                    line-height: 1.75rem;
                }
            }
            
            .film-error-camera-container {
                position: relative;
                width: 16rem;
                height: 16rem;
                margin-left: auto;
                margin-right: auto;
                margin-bottom: 2.5rem;
            }
            
            .film-error-camera {
                width: 100%;
                height: 100%;
                transition: transform 300ms;
                will-change: transform;
            }
            
            .film-error-camera-svg {
                width: 100%;
                height: 100%;
            }
            
            .film-error-code {
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 2rem;
            }
            
            .film-error-code-badge {
                padding: 0.5rem 1rem;
                border: 1px solid rgb(55, 65, 81);
                border-radius: 9999px;
                display: flex;
                align-items: center;
                backdrop-filter: blur(4px);
                background-color: rgba(0, 0, 0, 0.3);
            }
            
            .film-error-code-indicator {
                width: 0.5rem;
                height: 0.5rem;
                background-color: rgb(239, 68, 68);
                border-radius: 9999px;
                margin-right: 0.5rem;
                animation: pulse 2s infinite;
            }
            
            .film-error-code-text {
                font-size: 0.875rem;
                color: rgb(156, 163, 175);
            }
            
            .film-error-button {
                padding: 0.75rem 1.5rem;
                background-color: rgb(202, 138, 4);
                color: white;
                border-radius: 0.375rem;
                transition: all 300ms;
                display: flex;
                align-items: center;
                margin-left: auto;
                margin-right: auto;
                border: none;
                cursor: pointer;
            }
            
            .film-error-button:hover {
                background-color: rgb(234, 179, 8);
                transform: scale(1.05);
            }
            
            .film-error-button-icon {
                width: 1rem;
                height: 1rem;
                margin-right: 0.5rem;
            }
            
            .film-error-footer {
                position: absolute;
                bottom: 1.5rem;
                text-align: center;
                width: 100%;
            }
            
            .film-error-footer-text {
                color: rgb(107, 114, 128);
                font-size: 0.875rem;
            }
            
            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.5;
                }
            }
        `}</style>
    </div>
);
}
